import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { Logo } from "./Logo";

const LINKS = [
    { to: "/", label: "Home" },
    { to: "/about", label: "About" },
    { to: "/services", label: "Services" },
    { to: "/pricing", label: "Pricing" },
    { to: "/markets", label: "Live Markets" },
    { to: "/blog", label: "Insights" },
];

const Nav = () => {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setOpen(false);
    }, [location.pathname]);

    return (
        <>
            <header
                data-testid="site-nav"
                className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 border-b ${
                    scrolled
                        ? "bg-[#0A0A0C]/75 backdrop-blur-2xl border-white/10"
                        : "bg-transparent border-transparent"
                }`}
            >
                <div className="mx-auto max-w-7xl px-6 md:px-10 h-20 flex items-center justify-between">
                    <Logo />
                    <nav className="hidden lg:flex items-center gap-9">
                        {LINKS.map((l) => (
                            <NavLink
                                key={l.to}
                                to={l.to}
                                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                                className={({ isActive }) =>
                                    `text-sm tracking-wide transition-colors duration-300 ${
                                        isActive ? "text-gold" : "text-zinc-400 hover:text-white"
                                    }`
                                }
                            >
                                {l.label}
                            </NavLink>
                        ))}
                    </nav>
                    <div className="flex items-center gap-4">
                        <Link
                            to="/contact"
                            data-testid="nav-cta-contact"
                            className="hidden lg:inline-flex items-center gap-2 rounded-full border border-gold/40 px-6 py-2.5 text-sm text-gold transition-colors duration-300 hover:bg-gold hover:text-black"
                        >
                            Enquire <ArrowUpRight size={15} />
                        </Link>
                        <button
                            data-testid="nav-mobile-toggle"
                            onClick={() => setOpen((v) => !v)}
                            className="lg:hidden text-white p-2"
                            aria-label="Toggle menu"
                        >
                            {open ? <X size={24} /> : <Menu size={24} />}
                        </button>
                    </div>
                </div>
            </header>

            <AnimatePresence>
                {open && (
                    <motion.div
                        data-testid="nav-mobile-menu"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="fixed inset-0 z-40 bg-[#0A0A0C]/95 backdrop-blur-2xl lg:hidden flex flex-col justify-center px-10"
                    >
                        <nav className="flex flex-col gap-7">
                            {LINKS.map((l, i) => (
                                <motion.div
                                    key={l.to}
                                    initial={{ y: 24, opacity: 0 }}
                                    animate={{ y: 0, opacity: 1 }}
                                    transition={{ delay: 0.08 * i, duration: 0.5, ease: [0.76, 0, 0.24, 1] }}
                                >
                                    <NavLink
                                        to={l.to}
                                        data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                                        className={({ isActive }) =>
                                            `font-display text-4xl font-extrabold tracking-tight ${
                                                isActive ? "text-gold" : "text-white"
                                            }`
                                        }
                                    >
                                        {l.label}
                                    </NavLink>
                                </motion.div>
                            ))}
                            <motion.div
                                initial={{ y: 24, opacity: 0 }}
                                animate={{ y: 0, opacity: 1 }}
                                transition={{ delay: 0.45, duration: 0.5, ease: [0.76, 0, 0.24, 1] }}
                            >
                                <Link
                                    to="/contact"
                                    data-testid="nav-mobile-cta-contact"
                                    className="mt-4 inline-flex items-center gap-2 rounded-full border border-gold/40 px-8 py-3 text-gold"
                                >
                                    Enquire <ArrowUpRight size={16} />
                                </Link>
                            </motion.div>
                        </nav>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};

export default Nav;
